import { pick } from 'lodash';

import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { formatDateForServer, removeNullValuesNestedObject } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuotePart } from 'quotes/types';
import { TaxonomySource } from 'taxonomy/enums';

import { QuotePartUpdateBulkField } from './enums';
import { QuotePartUpdateBulkFormData } from './types';

export const mapQuotePartUpdateBulkFormData = (
  quoteParts: ICustomerQuotePart[],
  data: QuotePartUpdateBulkFormData
) => {
  const {
    editReplacementCost,
    editReplacementCostNumber,
    editManufacturer,
    editCorporateLocation,
    editPricingDate,
    editPricingStrategy,
    editPrioritizeLegacyCost,
    editPurchaseCost,
    editVendorQuoteNumber,
    editPrice,
    editMargin,
    editPriceBasedOn,
    editShipmentType,
    editTaxonomy,
    replacementCost,
    replacementCostNumber,
    manufacturer,
    corporateLocation,
    margin,
    price,
    priceBasedOn,
    pricingDate,
    pricingStrategy,
    prioritizeLegacyCost,
    purchaseCost,
    vendorQuoteNumber,
    shipmentType,
    taxonomy,
    ...restData
  } = data;

  const marginData = editMargin
    ? { gpm: margin / 100, gpmType: PricingHelper.PRICING_TYPE_TOTAL }
    : {};

  const priceData = editPrice ? { price } : {};

  const purchaseCostData = editPurchaseCost ? { purchaseCost, isCostingCustom: 1 } : {};

  const costSourceData = editVendorQuoteNumber
    ? {
        isCostingCustom: 1,
        costSource: { vendorQuoteNumber },
      }
    : {};

  const replacementCostData = editReplacementCost ? { replacementCost } : {};

  const replacementCostNumberData = editReplacementCostNumber ? { replacementCostNumber } : {};

  const shipmentTypeData = editShipmentType ? { shipmentTypeId: shipmentType.id } : {};
  const corporateLocationData = editCorporateLocation
    ? { corporateLocationId: corporateLocation.id }
    : {};

  const taxonomyData = editTaxonomy
    ? {
        taxonomyGroupId: taxonomy?.id || null,
        taxonomySource: TaxonomySource.MANUAL,
      }
    : null;

  const manufacturerData = editManufacturer
    ? {
        manufacturerId: manufacturer?.isNew ? `__tmp__${manufacturer.name}` : manufacturer.id,
      }
    : null;

  const prioritizeLegacyCostData = editPrioritizeLegacyCost
    ? { prioritizeLegacyCost: prioritizeLegacyCost ? 1 : 0 }
    : {};

  const pricingDateData = editPricingDate
    ? { pricingDate: formatDateForServer(pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null }
    : {};

  const pricingStrategyData = editPricingStrategy
    ? { pricingStrategy: pricingStrategy ?? null }
    : {};

  const mappedData = {
    ...removeNullValuesNestedObject({
      ...pick(restData, Object.values(QuotePartUpdateBulkField)),
      ...purchaseCostData,
      ...costSourceData,
      ...replacementCostData,
      ...replacementCostNumberData,
      ...priceData,
      ...marginData,
      ...shipmentTypeData,
      ...corporateLocationData,
      ...taxonomyData,
      ...manufacturerData,
      ...prioritizeLegacyCostData,
    }),
    ...pricingDateData,
    ...pricingStrategyData,
  };

  const getPriceBasedOnDataForLine = (quotePart: ICustomerQuotePart) => {
    if (!editPriceBasedOn) {
      return {};
    }

    if (QuotePartHelper.isPriceLocked(quotePart)) {
      return {};
    }

    if (priceBasedOn === PriceBasedOn.REPLACEMENT) {
      return QuotePartHelper.isEligibleForReplacementPricing(quotePart) ? { priceBasedOn } : {};
    }

    return { priceBasedOn };
  };

  return {
    quoteParts: quoteParts
      .map((quotePart) => ({
        id: quotePart.id,
        ...mappedData,
        ...getPriceBasedOnDataForLine(quotePart),
      }))
      .filter((linePayload) => Object.keys(linePayload).length > 1),
  };
};
